import type { AccessPlace } from '@church/domain'
import {
  ACCESS_APP_LABELS,
  ACCESS_MODULE_LABELS,
  appOf,
  ASSIGNABLE_MODULES,
  grantOf,
  grantPlaceKey,
  LEVEL_LABELS,
  levelDescription,
  parseAccessGrant,
  type AccessGrant,
  type AccessModule,
  type GrantScope,
  type Level,
} from '@church/permissions'
import { useState } from 'react'
import { useServiceAccess } from './useAccess.ts'

/** A place, with the key a select keeps it under. */
export interface PlaceOption extends AccessPlace {
  key: string
  scope: GrantScope
}

const scopeOfPlace = (place: AccessPlace): GrantScope =>
  place.kind === 'org' || place.id === null ? { kind: 'org' } : { kind: place.kind, id: place.id }

export const placeKey = (scope: GrantScope): string =>
  scope.kind === 'org' ? 'org' : `${scope.kind}:${scope.id}`

/** Places as offered: indented from the shallowest one offered, not from the church. */
export function outdent(places: PlaceOption[]): PlaceOption[] {
  const least = Math.min(...places.map((place) => place.depth))
  return places.map((place) => ({ ...place, depth: place.depth - least }))
}

/** A place as a select offers it. */
export function toPlaceOption(place: AccessPlace): PlaceOption {
  const scope = scopeOfPlace(place)
  return { ...place, scope, key: placeKey(scope) }
}

/** A level as the screens offer it: its name and what it adds. */
export interface LevelOption {
  level: Level
  label: string
  description: string
}

export function levelOptions(module: AccessModule, levels: readonly Level[]): LevelOption[] {
  return levels.map((level) => ({
    level,
    label: LEVEL_LABELS[level],
    description: levelDescription(module, level),
  }))
}

/**
 * The choices of a grant's panel (docs/13-ACCESS.md, "Assigning access"):
 * which module, where, and which level — offering only what the signed-in
 * person may give (`canGrant`; the server checks again). `place` fixes the
 * place (a folder's or service type's Access list); `editing` starts from a
 * grant someone holds. `resetKey` reloads the choices when the panel opens
 * on something else (null while closed).
 */
export function useGrantForm(input: {
  resetKey: string | null
  place?: GrantScope
  editing?: {
    app: string
    module: string
    level: string
    scopeType: string
    scopeId: string | null
  } | null
}) {
  const access = useServiceAccess()
  const start = input.editing ? parseAccessGrant(input.editing) : null
  const [chosenModule, setModule] = useState<AccessModule>(start?.module ?? 'planning')
  const [chosenPlace, setChosenPlace] = useState<string | null>(null)
  const [chosenLevel, setChosenLevel] = useState<Level | null>(null)
  const [loadedFor, setLoadedFor] = useState<string | null>(null)
  if (input.resetKey !== loadedFor) {
    setLoadedFor(input.resetKey)
    setModule(start?.module ?? 'planning')
    setChosenPlace(start ? placeKey(start.scope) : null)
    setChosenLevel(start?.level ?? null)
  }

  /** The places a module's access can be given, where they may give at least one rung. */
  const placesFor = (candidate: AccessModule): PlaceOption[] =>
    outdent(
      access.places
        .filter((place) => candidate === 'planning' || place.kind === 'org')
        .map(toPlaceOption)
        .filter((place) => access.grantable(candidate, place.scope).length > 0),
    )

  const modules = ASSIGNABLE_MODULES.filter(
    (candidate) =>
      candidate === start?.module ||
      // A folder's or service type's Access list gives planning only: the rest are church-wide.
      ((!input.place || candidate === 'planning') &&
        (input.place
          ? access.grantable(candidate, input.place).length > 0
          : placesFor(candidate).length > 0)),
  )
  // The module chosen, or — when they cannot give that one — the first they can.
  const module = modules.includes(chosenModule) ? chosenModule : (modules[0] ?? chosenModule)
  const places = input.place ? [] : placesFor(module)
  const scope: GrantScope | null =
    input.place ??
    places.find((place) => place.key === chosenPlace)?.scope ??
    places[0]?.scope ??
    null
  const levels = scope ? levelOptions(module, access.grantable(module, scope)) : []
  // A level they may not give here (another place, another module) is no choice at all.
  const level = levels.some((option) => option.level === chosenLevel) ? chosenLevel : null
  const grant: AccessGrant | null = scope && level ? grantOf(module, level, scope) : null

  return {
    ready: access.ready,
    modules: modules.map((id) => ({ id, label: moduleLabel(id) })),
    /** The same, grouped by app for the "What" select: ForServices, then ForChurch. */
    moduleGroups: (['services', 'church'] as const)
      .map((app) => ({
        app: ACCESS_APP_LABELS[app],
        options: modules
          .filter((id) => appOf(id) === app)
          .map((id) => ({ id, label: ACCESS_MODULE_LABELS[id] })),
      }))
      .filter((group) => group.options.length > 0),
    module,
    setModule: (next: AccessModule) => {
      setModule(next)
      setChosenPlace(null)
    },
    /** Where (when the place is not fixed): an indented list. */
    places,
    placeKey: scope ? placeKey(scope) : '',
    setPlace: setChosenPlace,
    /** Only church-wide for this module: no choice of place to show. */
    churchWideOnly: module !== 'planning',
    /** Why there is no "Where" for a ForChurch module, in one line; null otherwise. */
    churchWideNote:
      appOf(module) === 'church'
        ? 'Church-wide: ForChurch access is never limited to a folder.'
        : null,
    levels,
    level,
    setLevel: setChosenLevel,
    /** The grant as chosen; null until a level is. */
    grant,
    /** The chosen grant would move the one being edited to another module or place. */
    moves: start !== null && grant !== null && grantPlaceKey(start) !== grantPlaceKey(grant),
    /** Nothing changed from the grant being edited. */
    unchanged:
      start !== null &&
      grant !== null &&
      grantPlaceKey(start) === grantPlaceKey(grant) &&
      start.level === grant.level,
    /** The grant being edited may be taken away by them. */
    removable: start !== null && access.canGive(start),
    placeLabel: access.placeLabel,
  }
}

const moduleLabel = (module: AccessModule): string =>
  `${appOf(module) === 'services' ? 'ForServices' : 'ForChurch'} · ${ACCESS_MODULE_LABELS[module]}`
